import { useRef, useState } from 'react';
import { useOverlayContext } from 'seagull';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { useStoreActions } from '~/store/store-context';
import { BillClientsResultsResponseType } from '~/types/api/billing/bulk/bill-clients';
export const useBulkBillClients = (param)=>{
    let { onSuccess, onError, testIds } = param;
    const { api, logBI } = useStoreActions();
    const { showSuccessToast, showErrorToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const isJobInProgress = useRef(false);
    const [progress, setProgress] = useState(undefined);
    const { mutate, isLoading, data: billResults, isIdle, reset } = useMutationState({
        mutationFn: async (bulkUploadId)=>{
            if (!bulkUploadId) {
                return null;
            }
            logBI(18704);
            await api.billing.bulk.billClients(bulkUploadId);
            isJobInProgress.current = true;
            let _progress;
            while(isJobInProgress.current){
                const jobResponse = await api.billing.bulk.getBillClientsResult(bulkUploadId);
                if (jobResponse.type === BillClientsResultsResponseType.InProgress) {
                    _progress = {
                        completed: jobResponse.inProgress.invoicesCompletedCount,
                        total: jobResponse.inProgress.totalInvoicesCount
                    };
                    setProgress(_progress);
                } else {
                    isJobInProgress.current = false;
                    if (jobResponse.type === BillClientsResultsResponseType.Succeeded && (_progress === null || _progress === void 0 ? void 0 : _progress.total) && (_progress === null || _progress === void 0 ? void 0 : _progress.total) > 0) {
                        setProgress({
                            completed: _progress === null || _progress === void 0 ? void 0 : _progress.total,
                            total: _progress === null || _progress === void 0 ? void 0 : _progress.total
                        });
                        return jobResponse;
                    }
                    setProgress(undefined);
                    return jobResponse;
                }
            }
            return null;
        },
        onSuccess: (data)=>{
            if (!data) {
                return;
            }
            if ((data === null || data === void 0 ? void 0 : data.type) !== BillClientsResultsResponseType.Failed) {
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
                logBI(18705);
                showSuccessToast(commonTexts.toasts.clientsBilledSuccessfully(), testIds.SuccessToast);
            }
        },
        onError: (e)=>{
            try {
                resetBulkBillClients();
                onError === null || onError === void 0 ? void 0 : onError();
                const error = new ErrorResponse(e);
                if (error.hasServerError(ServerError.BulkBillAlreadyInProgress)) {
                    showErrorToast(commonTexts.toasts.bulkBillingAlreadyInProgress(), testIds.ErrorToast);
                } else {
                    showErrorToast(commonTexts.toasts.somethingWentWrongPleaseContactSupport(), testIds.ErrorToast);
                }
            } catch (e) {
                showErrorToast(commonTexts.toasts.somethingWentWrongPleaseContactSupport(), testIds.ErrorToast);
            }
        }
    });
    const resetBulkBillClients = ()=>{
        reset();
        setProgress(undefined);
        isJobInProgress.current = false;
    };
    return {
        billClients: mutate,
        billResults,
        progress,
        isLoading: isLoading || (billResults === null || billResults === void 0 ? void 0 : billResults.type) === BillClientsResultsResponseType.Succeeded,
        reset: resetBulkBillClients,
        wasBillClientsClicked: !isIdle
    };
};
